/*
 * collection-fw26.js
 * Goldwin 2026 Fall & Winter Collection（元 assets/collection/2026fw/js/index.js）
 * eu/us/uk/kr/asia/jp の6ストア × fall/winter 2ページで共有する自己完結JS。
 *
 * 元コードは ES module (utility.js / section-observer.v2.js / modal.v3.js) を import していたが、
 * Shopify Page の自己完結スクリプトとして動くよう、実際に使用している関数・クラスのみを
 * このファイルにインライン化した（未使用だった import: notifyToAnalytics, isEnv, smoothScroll,
 * exchangeAttr, calcSlidesPerView, getHeight は元コードでも呼ばれていなかったため省略）。
 *
 * Fall⇔Winter のタブ切替のみ、WPのURLパターン（/{region}/{fetchPath}/）から
 * Shopify Page の絶対URL（data-fetch-url="/pages/collection-fw26-winter" 等）を直接使う方式に変更。
 * 他のロジック（フェードアニメーション・pushState・DOMParserによる#PG_CONTENT差し替え等）は踏襲。
 *
 * 動画（<video data-video-file="...">）は Shopify Files アップロード後の実CDNパスが
 * 事前に組み立てられないため、テンプレート側で window.__fileUrlMap に file_url 解決済みURLを
 * 埋め込み、このJSが実行時に video.src へ注入する。
 */
(function () {
  "use strict";

  /* ============================== utility ============================== */

  function clamp(num, min, max) {
    if (min === undefined) min = 0;
    if (max === undefined) max = 1;
    return Math.min(Math.max(num, min), max) || 0;
  }

  function offset(elem) {
    if (!elem) return;
    var result = {};
    var rect = elem.getBoundingClientRect();

    result.top = rect.top + window.scrollY;
    result.left = rect.left + window.scrollX;
    result.right = rect.left + window.scrollX + elem.clientWidth;
    result.bottom = rect.top + window.scrollY + elem.clientHeight;

    return result;
  }

  function getCustomProperty(_selector, _property, removeExtension) {
    if (_selector === undefined) _selector = document.documentElement;
    var element =
      typeof _selector === "object" ? _selector : document.querySelector(_selector);
    var style = getComputedStyle(element);
    var value = String(style.getPropertyValue(_property)).trim();
    if (removeExtension) value = parseInt(value, 10);

    return value;
  }

  var $$ = {
    breakpoint1: 480,
    breakpoint2: 768,
  };

  function isDevice(type) {
    var isPC = window.matchMedia("(min-width: " + ($$.breakpoint2 + 1) + "px)").matches;
    var isTB = window.matchMedia(
      "(min-width: " + ($$.breakpoint1 + 1) + "px) and (max-width: " + $$.breakpoint2 + "px)"
    ).matches;
    var isSP = window.matchMedia("(max-width: " + $$.breakpoint1 + "px)").matches;

    if (!type) {
      if (isPC) return "PC";
      if (isTB) return "TB";
      if (isSP) return "SP";
    } else {
      if (type === "PC") return isPC;
      if (type === "TB") return isTB;
      if (type === "SP") return isSP;
    }
  }

  function debounce(fn, delay) {
    if (delay === undefined) delay = 200;
    var timeout;
    return function () {
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        fn.apply(null, args);
      }, delay);
    };
  }

  function createElement(tagName, attributes) {
    if (tagName === undefined) tagName = "div";
    var newEl = document.createElement(tagName);
    if (attributes) {
      for (var key in attributes) {
        if (Object.prototype.hasOwnProperty.call(attributes, key)) {
          newEl.setAttribute(key, attributes[key]);
        }
      }
    }
    return newEl;
  }

  /* ========================= SectionObserver ========================= */
  /* 元 js/modules/section-observer.v2.js をクラス構文のままインライン化 */

  class SectionObserver {
    constructor(selector, args) {
      args = args || {};
      this.selector = selector;
      this.selectorAll = document.querySelectorAll(selector);

      this.inset = args.inset ?? 0;

      this.decimalPlaces = 8;

      this.type = Object.assign(
        {
          class: true,
          progress: ["all"],
        },
        args.type
      );
    }

    observe() {
      this.selectorAll.forEach((selector) => {
        const progresses = this.getProgress(selector);
        if (this.type.class) this.setClasses(selector, progresses);
        if (this.type.progress && this.type.progress.length > 0) {
          this.type.progress.forEach((type) => {
            this.setProgress(selector, progresses, type);
          });
        }
      });
    }

    getProgress(section) {
      const progresses = {};

      const baseline = window.scrollY - this.inset;
      const isSectionTallerThanWindow = section.clientHeight > window.innerHeight;

      progresses.enter = clamp(
        (baseline + window.innerHeight - offset(section).top) / window.innerHeight
      );

      progresses.partial = clamp(
        (baseline + window.innerHeight - offset(section).top) / section.clientHeight
      );

      progresses.stay = isSectionTallerThanWindow
        ? 0
        : clamp(
            (baseline + window.innerHeight - offset(section).bottom) /
              (window.innerHeight - section.clientHeight)
          );

      progresses.exit = clamp(
        (baseline - offset(section).top) / section.clientHeight
      );

      progresses.leave = clamp(
        (baseline + window.innerHeight - offset(section).bottom) / window.innerHeight
      );

      progresses.intersect = clamp(
        (baseline + window.innerHeight - offset(section).top) /
          (section.clientHeight + window.innerHeight)
      );

      return progresses;
    }

    setClasses(section, progresses) {
      const { enter, stay, exit, leave, intersect } = progresses;

      section.classList.toggle("is-entering", enter && enter < 1);
      section.classList.toggle("is-entered", enter === 1);

      section.classList.toggle("is-leaving", leave && leave < 1);
      section.classList.toggle("is-leaved", leave === 1);
      section.classList.toggle("is-full-contain", stay && stay < 1);
      section.classList.toggle("is-intersecting", intersect && intersect < 1);
    }

    setProgress(section, progresses, type) {
      if (type === "all") {
        Object.keys(progresses).forEach((key) => {
          const roundedProgress = progresses[key].toFixed(this.decimalPlaces);
          section.style.setProperty(`--${key}Progress`, roundedProgress);
        });
      } else {
        const roundedProgress = progresses[type].toFixed(this.decimalPlaces);
        section.style.setProperty(`--${type}Progress`, roundedProgress);
      }
    }

    reset(args) {
      const cleanup = args && "cleanup" in args ? args.cleanup : true;

      if (cleanup) {
        this.selectorAll.forEach((section) => {
          section.classList.remove(
            "is-entering",
            "is-entered",
            "is-leaving",
            "is-leaved",
            "is-full-contain",
            "is-intersecting"
          );

          ["enter", "partial", "stay", "exit", "leave", "intersect"].forEach((key) => {
            section.style.removeProperty(`--${key}Progress`);
          });
        });
      }

      this.selectorAll = document.querySelectorAll(this.selector);
      this.observe();

      return this;
    }
  }

  /* =============================== Modal =============================== */
  /* 元 js/modules/modal.v3.js をクラス構文のままインライン化 */

  class Modal {
    constructor(selector, args) {
      selector = selector || "#LayoutModal";
      args = args || {};

      this.selector = selector;

      this.settings = Object.assign(
        {
          duration: 800,
          appendTo: "body",
          bodyFixed: true,
          templateSelector: "#MODAL_TEMPLATE",
          openedFn: null,
          closedFn: null,
        },
        args
      );

      this.$el = {};

      this._parent = document.querySelector(this.settings.appendTo) || document.body;

      this.isOpened = false;
      this.isOpening = false;
      this.isClosing = false;

      this.prevActiveElement = null;
      this.focusableElements = [];

      this._focusTrapHandler = this.trapFocus.bind(this);

      this.initialize();
    }

    initialize() {
      this.createModal();
      this.setModal();
      this.setEvents();
    }

    setModal() {
      this.$el.root = document.querySelector(this.selector);

      if (!this.$el.root) {
        throw new Error(`モーダルが見つかりません: ${this.selector}`);
      }

      this.$el.container = this.$el.root.querySelector("[data-modal-container]");
      this.$el.close = this.$el.root.querySelector("[data-modal-close]");
      this.$el.content = this.$el.root.querySelector("[data-modal-content]");

      if (!this.$el.container || !this.$el.close || !this.$el.content) {
        throw new Error(`${this.settings.templateSelector} 内に必要なdata属性がありません`);
      }
    }

    createModal() {
      let $modal = document.querySelector(this.selector);

      if (!$modal) {
        $modal = createElement("div", {
          id: this.selector.replace(/^#/, ""),
          role: "dialog",
          "aria-hidden": "true",
        });

        $modal.style.display = "none";
        this._parent.append($modal);
      }

      this.renderTemplate($modal);
    }

    renderTemplate($modal = this.$el.root) {
      const $template = document.querySelector(this.settings.templateSelector);

      if (!($template instanceof HTMLTemplateElement)) {
        throw new Error(`template要素が見つかりません: ${this.settings.templateSelector}`);
      }

      $modal.replaceChildren($template.content.cloneNode(true));
    }

    setEvents() {
      this.$el.root.addEventListener("click", (event) => {
        if (!(event.target instanceof Element)) return;

        const isCloseButton = event.target.closest("[data-modal-close]");
        const isOutsideContent = !event.target.closest("[data-modal-content]");

        if (isCloseButton || isOutsideContent) {
          this.close();
        }
      });

      this.$el.root.addEventListener("keydown", this._focusTrapHandler);

      window.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && this.isOpened) {
          this.close();
        }
      });
    }

    open() {
      if (this.isOpened || this.isOpening || this.isClosing) return;

      this.isOpening = true;
      this.prevActiveElement = document.activeElement;

      this.$el.root.style.display = "block";
      this.$el.root.setAttribute("aria-modal", "true");
      this.$el.root.setAttribute("aria-hidden", "false");

      const animation = this.$el.root.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: this.settings.duration,
        easing: "ease-out",
        fill: "forwards",
      });

      animation.onfinish = () => {
        this.isOpened = true;
        this.isOpening = false;

        this.updateFocusableElements();
        this.$el.close.focus();

        this.settings.openedFn?.();

        if (this.settings.bodyFixed) {
          window.dispatchEvent(new Event("bodyFixedOn"));
        }
      };
    }

    close() {
      if (!this.isOpened || this.isClosing) return;

      this.isClosing = true;

      const animation = this.$el.root.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: this.settings.duration,
        easing: "ease-out",
        fill: "forwards",
      });

      animation.onfinish = () => {
        this.isOpened = false;
        this.isClosing = false;

        this.$el.root.style.display = "none";
        this.$el.root.removeAttribute("aria-modal");
        this.$el.root.setAttribute("aria-hidden", "true");

        this.focusableElements = [];

        if (this.prevActiveElement instanceof HTMLElement && this.prevActiveElement.isConnected) {
          this.prevActiveElement.focus();
        }

        this.renderTemplate();
        this.setModal();

        this.settings.closedFn?.();

        if (this.settings.bodyFixed) {
          window.dispatchEvent(new Event("bodyFixedOff"));
        }

        this.$el.root.dispatchEvent(new Event("modalClose"));
      };
    }

    updateFocusableElements() {
      this.focusableElements = [
        ...this.$el.root.querySelectorAll(
          `
          button:not([disabled]),
          [href],
          input:not([disabled]),
          select:not([disabled]),
          textarea:not([disabled]),
          [tabindex]:not([tabindex="-1"])
        `
        ),
      ];
    }

    trapFocus(event) {
      if (event.key !== "Tab" || !this.isOpened || this.focusableElements.length === 0) {
        return;
      }

      const first = this.focusableElements[0];
      const last = this.focusableElements[this.focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /* ============================= page logic ============================= */
  /* 元 assets/collection/2026fw/js/index.js のロジック（フェッチURL周りのみ変更） */

  const pageTop = document.getElementById("PAGE_TOP");
  const pageScreen = document.getElementById("PAGE_SCREEN");

  let looksObserver = new SectionObserver(".pg-looks", {
    type: {
      class: true,
      progress: false,
    },
    inset: getCustomProperty(document.documentElement, "--fixed-header-height", true) * -1,
  });

  const contentsObserver = new SectionObserver(".pg-contents", {
    type: {
      progress: false,
    },
  });

  function setupModal() {
    const modalTrigger = document.getElementById("LOOK_MODAL_TRIGGER");

    if (!modalTrigger) return;

    const modal = new Modal("#LookModal", {
      templateSelector: "#MODAL_TEMPLATE",
      bodyFixed: false,
      openedFn: () => {
        document.documentElement.style.scrollBehavior = "auto";
      },
      closedFn: () => {
        document.documentElement.style.scrollBehavior = "";
      },
    });

    modalTrigger.addEventListener("click", () => {
      modal.open();
    });

    document.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;

      const thumbnail = event.target.closest("#LookModal .pg-look-thumbnail");

      if (!thumbnail || !modal.isOpened) return;

      modal.close();
    });
  }

  function setupTabSwitcher() {
    const root = document.querySelector(".pg-tab-switcher");
    if (!root) return;
    const itemAll = root.querySelectorAll(".pg-tab-switcher__item");

    let activeItem = [...itemAll].find((item) => item.classList.contains("is-active"));

    const updatePosition = (target) => {
      root.style.setProperty("--tab-switcher-left", `${target.offsetLeft}px`);
      root.style.setProperty("--tab-switcher-width", `${target.clientWidth}px`);
    };

    updatePosition(activeItem);

    itemAll.forEach((item) => {
      item.addEventListener("click", () => {
        if (activeItem === item) return;
        activeItem.classList.remove("is-active");
        item.classList.add("is-active");

        activeItem = item;
        updatePosition(activeItem);
        item.querySelector("button").focus();
      });
    });

    window.addEventListener("resized", () => updatePosition(activeItem));
  }

  function tabSwitcherFixed() {
    const tabSwitcher = document.querySelector(".pg-tab-switcher");
    if (!tabSwitcher) return;

    const fixedPoint =
      offset(tabSwitcher).top -
      getCustomProperty(document.documentElement, "--fixed-header-height", true) -
      20;

    if (!isDevice("SP")) {
      tabSwitcher.classList.toggle("is-fixed", window.scrollY > fixedPoint);
    } else {
      tabSwitcher.classList.toggle("is-hide", window.scrollY > fixedPoint);
    }
  }

  function setupLookCounter() {
    const currentCounter = document.querySelector(".pg-look-counter__current");
    const totalCounter = document.querySelector(".pg-look-counter__total");
    const counterLabel = document.querySelector(".pg-look-counter");

    let lookAll = [...document.querySelectorAll(".pg-look")];
    let currentIndex = 0;
    let ticking = false;

    if (!currentCounter || !totalCounter || !counterLabel) return;
    if (!lookAll.length) return;

    const getDigitLength = () => {
      return Math.max(2, String(lookAll.length).length);
    };

    const formatNumber = (number) => {
      return String(number).padStart(getDigitLength(), "0");
    };

    const initializeCounter = (element, value) => {
      element.textContent = "";

      [...value].forEach((number) => {
        const digit = document.createElement("span");
        const currentNumber = document.createElement("span");

        digit.className = "pg-look-counter__digit";
        currentNumber.className = "pg-look-counter__number is-current";
        currentNumber.textContent = number;

        digit.append(currentNumber);
        element.append(digit);
      });
    };

    const updateCounter = (element, value, direction = "down") => {
      const digits = element.querySelectorAll(".pg-look-counter__digit");

      if (digits.length !== value.length) {
        initializeCounter(element, value);
        return;
      }

      [...value].forEach((number, digitIndex) => {
        const digit = digits[digitIndex];

        if (!digit) return;

        const currentNumber = digit.querySelector(".pg-look-counter__number.is-current");

        if (!currentNumber) return;
        if (currentNumber.textContent === number) return;

        digit.querySelectorAll(".is-leave-up, .is-leave-down").forEach((oldNumber) => oldNumber.remove());

        const nextNumber = document.createElement("span");
        const enterClass = direction === "down" ? "is-next" : "is-prev";
        const leaveClass = direction === "down" ? "is-leave-up" : "is-leave-down";

        nextNumber.className = `pg-look-counter__number ${enterClass}`;
        nextNumber.textContent = number;

        digit.append(nextNumber);

        nextNumber.getBoundingClientRect();

        requestAnimationFrame(() => {
          currentNumber.classList.remove("is-current");
          currentNumber.classList.add(leaveClass);

          nextNumber.classList.remove(enterClass);
          nextNumber.classList.add("is-current");
        });

        const removeOldNumber = () => {
          if (currentNumber.isConnected) {
            currentNumber.remove();
          }
        };

        currentNumber.addEventListener(
          "transitionend",
          (event) => {
            if (event.propertyName !== "transform") return;
            removeOldNumber();
          },
          { once: true }
        );

        window.setTimeout(removeOldNumber, 600);
      });
    };

    const updateAccessibleLabel = () => {
      counterLabel.setAttribute("aria-label", `${lookAll.length}件中${currentIndex + 1}件目`);
    };

    const initialize = () => {
      initializeCounter(currentCounter, formatNumber(1));
      initializeCounter(totalCounter, formatNumber(lookAll.length));
      updateAccessibleLabel();
    };

    const handleScroll = () => {
      lookAll.forEach((look, index) => {
        const position = offset(look);

        if (window.scrollY > position.top && window.scrollY <= position.bottom && currentIndex !== index) {
          const direction = index > currentIndex ? "down" : "up";

          currentIndex = index;

          updateCounter(currentCounter, formatNumber(index + 1), direction);

          updateAccessibleLabel();
        }
      });

      ticking = false;
    };

    initialize();
    handleScroll();

    window.addEventListener(
      "scroll",
      () => {
        if (ticking) return;

        ticking = true;
        requestAnimationFrame(handleScroll);
      },
      { passive: true }
    );
  }

  function setupCredits() {
    const creditsAll = document.querySelectorAll(".pg-credits");

    creditsAll.forEach((credits) => {
      const button = credits.querySelector(".pg-credits__more");
      const thirdCredit = credits.querySelector(".pg-credit:nth-child(3)");

      if (!button || !thirdCredit) return;

      button.addEventListener("click", () => {
        if (credits.classList.contains("is-open")) return;

        const startHeight = credits.offsetHeight;

        credits.classList.add("is-open");
        button.setAttribute("aria-expanded", "true");

        const endHeight = credits.offsetHeight;

        const animation = credits.animate(
          [
            {
              height: `${startHeight}px`,
              overflow: "hidden",
            },
            {
              height: `${endHeight}px`,
              overflow: "hidden",
            },
          ],
          {
            duration: 500,
            easing: "cubic-bezier(0.22, 1, 0.36, 1)",
          }
        );

        animation.onfinish = () => {
          credits.style.removeProperty("height");
          credits.style.removeProperty("overflow");
        };
      });
    });
  }

  /**
   * 動画の src は事前にURLを組み立てられないため、
   * テンプレート側で file_url 解決済みの window.__fileUrlMap を参照して実行時に設定する。
   */
  function setupVideoSrc() {
    document.querySelectorAll("[data-video-file]").forEach((video) => {
      const fileName = video.dataset.videoFile;
      const url = window.__fileUrlMap && window.__fileUrlMap[fileName];
      if (url && video.getAttribute("src") !== url) {
        video.src = url;
      }
    });
  }

  function renderContent() {
    const pageTop = document.getElementById("PAGE_TOP");
    const content = document.getElementById("PG_CONTENT");
    const triggers = document.querySelectorAll("[data-fetch-url]");

    const animationSettings = {
      duration: 400,
      easing: "ease-in-out",
      fill: "forwards",
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener("click", async () => {
        const screenFadeIn = pageScreen.animate(
          [
            {
              opacity: 0,
              visibility: "hidden",
            },
            {
              opacity: 1,
              visibility: "visible",
            },
          ],
          animationSettings
        );

        screenFadeIn.onfinish = async () => {
          // 元コードは `${location.origin}/${region}/${trigger.dataset.fetchPath}/` のように
          // WPのURLパターンを再構築していたが、Shopify Page には地域パスが無いため、
          // data-fetch-url に直接埋め込んだ絶対パス（例: /pages/collection-fw26-winter）をそのまま使う。
          const url = trigger.dataset.fetchUrl;

          const result = await fetchContentData(url);
          window.scrollTo({
            top: pageTop.offsetTop,
            left: 0,
          });

          content.innerHTML = result.innerHTML;

          const screenFadeOut = pageScreen.animate(
            [
              {
                opacity: 1,
                visibility: "visible",
              },
              {
                opacity: 0,
                visibility: "hidden",
              },
            ],
            animationSettings
          );

          screenFadeOut.onfinish = () => {
            handlePageRendered();
          };

          history.pushState(
            {
              target: url,
            },
            "",
            url
          );
        };
      });
    });
  }

  async function fetchContentData(url) {
    const result = await fetch(url);
    const html = await result.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const acquired = doc.querySelector("#PG_CONTENT");

    return acquired;
  }

  const handlePageRendered = () => {
    tabSwitcherFixed();
    setupLookCounter();
    setupModal();
    setupVideoSrc();

    requestAnimationFrame(() => {
      looksObserver.reset();
    });
  };

  const handleWindowReady = () => {
    setupTabSwitcher();
    setupLookCounter();
    setupModal();
    setupCredits();
    setupVideoSrc();
    renderContent();
    contentsObserver.observe();
  };
  const handleWindowLoad = () => {
    tabSwitcherFixed();
    looksObserver.observe();
  };
  const handleWindowScroll = () => {
    tabSwitcherFixed();
    looksObserver.observe();
    contentsObserver.observe();
  };
  const handleWindowResize = () => {
    debounce(() => {}, 200);
  };

  const handlewindowResized = () => {
    tabSwitcherFixed();
  };

  window.addEventListener("DOMContentLoaded", handleWindowReady);
  window.addEventListener("load", handleWindowLoad);
  window.addEventListener("scroll", handleWindowScroll);
  window.addEventListener("resize", handleWindowResize);
  window.addEventListener("resized", handlewindowResized);
  window.addEventListener("resizedX", () => {});

  window.addEventListener("popstate", (e) => {
    if (!e.state) return;
    document.querySelector(`[data-fetch-url="${e.state.target}"]`)?.click();
  });
})();
